import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [checked, setChecked] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth callback -> skip /me, AuthCallback handles it
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      return;
    }
    // Public routes (shareable project link) don't require auth -> skip /me to avoid 401 noise
    if (window.location.pathname.startsWith("/p/")) {
      setUser(false);
      setChecked(true);
      return;
    }
    refresh();
  }, [refresh]);

  const setUserExternal = (u) => {
    setUser(u);
    setChecked(true);
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checked, login, register, logout, refresh, setUserExternal, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
